import React from 'react'
import { Spin } from '@surecompanies/core_components'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { getCensusRunTrace } from './census-run-api'
import Section from './Section'
import { ACCENT } from './StageRail'
import { CensusTraceMatch, CensusTraceStage } from './types'

/** Stage labels, kept in step with the timeline's own list. */
const STAGE_NAMES: Record<string, string> = {
  RAW_FILE_RECEIVED: 'Raw File Received',
  MAPPING_APPLIED: 'Mapping Applied',
  ELIGIBILITY_FILTER: 'Eligibility Filter',
  COMPANY_IGNORE_RULES: 'Ignore Rules Loaded',
  VALIDATION: 'Validation Complete',
  SAVED_TO_DATABASE: 'Saved to Database',
  SUBMITTED_TO_CMV2: 'Submitted to CMv2',
}

/**
 * What each file is, in the reader's words.
 *
 * None of these is a verdict. An AMA run writes the error and ineligible rows
 * as files an agent can still submit, so landing in one of them is where the
 * row ended up, not a judgement that it failed.
 */
const SLOT_LABELS: Record<string, string> = {
  RAW: 'as received',
  MAPPED: 'mapped',
  ELIGIBLE: 'eligible',
  INELIGIBLE: 'ineligible',
  ERRORS: 'errors',
  FINAL: 'valid',
}

/** Files that need a person to decide something, shown in amber. */
const NEEDS_ATTENTION = ['ERRORS', 'INELIGIBLE']

const formatTime = (value?: string): string =>
  value ? dayjs(value).format('h:mm:ss A') : ''

/** The name is shown because a prefix search matches more than one person:
 *  "lancaster" finds both Adam and Eve, and the reader needs to tell them
 *  apart without opening the file. */
const describeMatch = (match: CensusTraceMatch): string => {
  const name = [match.firstName, match.lastName].filter(Boolean).join(' ')
  const where = match.employeeId
    ? `row ${match.position} · ${match.employeeId}`
    : `row ${match.position}`
  return name === '' ? where : `${name} · ${where}`
}

/**
 * Follows one person through a run.
 *
 * Shown only while a name filter is set, because it costs a read of every row
 * snapshot the run wrote. Positions are per file: a file that dropped rows
 * renumbers the ones that remain.
 */
function PersonTrace({
  runId,
  firstName,
  lastName,
}: {
  runId: string
  firstName: string
  lastName: string
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['census-run-trace', runId, firstName, lastName],
    queryFn: () =>
      getCensusRunTrace(runId, {
        firstName: firstName || undefined,
        lastName: lastName || undefined,
      }),
    staleTime: 60 * 1000,
  })

  const title = `Trace: ${[firstName, lastName].filter(Boolean).join(' ')}`

  if (isPending) {
    return (
      <Section title={title}>
        <div style={{ padding: '16px 0', textAlign: 'center' }}>
          <Spin />
        </div>
      </Section>
    )
  }

  if (isError) {
    return (
      <Section title={title}>
        <div style={{ padding: '12px 0', color: '#7a1f22' }}>
          Could not trace this person through the run.
        </div>
      </Section>
    )
  }

  if (!data?.hasSnapshots) {
    return (
      <Section title={title}>
        <div style={{ padding: '12px 0', color: '#6b7280' }}>
          This run wrote no row snapshots, so there is nothing to trace. That is
          not the same as the person being absent.
        </div>
      </Section>
    )
  }

  const stagesWithMatches = data.stages.filter((stage) =>
    stage.artifacts.some((artifact) => artifact.matches.length > 0)
  )

  if (stagesWithMatches.length === 0) {
    return (
      <Section title={title}>
        <div style={{ padding: '12px 0', color: '#6b7280' }}>
          No row in this run matched that name.
        </div>
      </Section>
    )
  }

  return (
    <Section title={title}>
      <div style={{ paddingTop: 4 }}>
        {stagesWithMatches.map((stage) => (
          <TraceRow key={stage.stepKey} stage={stage} />
        ))}
      </div>
      {data.unreadableFiles.length > 0 && (
        <div style={{ color: '#7a4d0a', fontSize: 12.5, paddingBottom: 12 }}>
          Could not read {data.unreadableFiles.join(', ')}, so those stages are
          not accounted for.
        </div>
      )}
    </Section>
  )
}

function TraceRow({ stage }: { stage: CensusTraceStage }) {
  const withMatches = stage.artifacts.filter(
    (artifact) => artifact.matches.length > 0
  )
  const needsAttention = withMatches.some((artifact) =>
    NEEDS_ATTENTION.includes(artifact.slot)
  )
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        padding: '8px 0',
        borderTop: stage.seq === 1 ? 'none' : '1px solid #f1f2f4',
      }}
    >
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          {STAGE_NAMES[stage.stepKey] ?? stage.stepKey}
        </div>
        {/* One line per matching row: two people sharing a surname read as one
            run-on line otherwise. */}
        {withMatches.map((artifact) =>
          artifact.matches.map((match) => (
            <div
              key={`${artifact.file}-${match.position}`}
              style={{
                fontSize: 12.5,
                color: NEEDS_ATTENTION.includes(artifact.slot)
                  ? '#7a4d0a'
                  : '#6b7280',
              }}
            >
              {SLOT_LABELS[artifact.slot] ?? artifact.slot.toLowerCase()}
              {' — '}
              <span style={{ fontFamily: 'monospace' }}>
                {describeMatch(match)}
              </span>
              <span style={{ color: '#9ca3af' }}> in {artifact.file}</span>
            </div>
          ))
        )}
      </div>
      <div
        style={{
          color: needsAttention ? '#7a4d0a' : ACCENT,
          fontSize: 12.5,
          whiteSpace: 'nowrap',
        }}
      >
        {formatTime(stage.endedAt)}
      </div>
    </div>
  )
}

export default PersonTrace
